import { AST, ASTAdditionalDataFromServer } from 'mk2/apps/wiki/schemas';
import { createLikeableSchema, LikeableEntity } from 'mk2/containers/Like/Like.schemas';
import { DeletedByRole } from 'mk2/helpers/deleteable';
import { Entity, PhotoEntity, PhotoSchema, UserDomainDataEntity, UserEntity, UserSchema } from 'mk2/schemas';
import { schema } from 'normalizr';

export enum PostVisibility {
    Public = 0,
    Authenticated = 1,
    Friends = 2,
    Private = 3,
    Group = 4,
}

export enum PostCommentability {
    CommentableByNobody = 0,
    CommentableByAuthenticated = 1,
}

export interface BlogArticleEntity extends Entity {
    // Deprecated
    user: UserEntity;
    author: UserEntity;

    titleAST: AST;
    titleASTAdditionalData: ASTAdditionalDataFromServer;
    perexAST: AST;
    perexASTAdditionalData: ASTAdditionalDataFromServer;
    contentAST: AST;
    contentASTAdditionalData: ASTAdditionalDataFromServer;
    scripts: string;
    countTopicContributors: {[topicSlug: string]: number};
    slug: string;
    photos: PhotoEntity[];
    viewsCount: number;
    createdAt: string;

    blockedAt: string;
    blockedReason: string;
    blockedBy: string;

    deletedAt: string;
    deletedReason: string;
    deletedBy: UserEntity;
    deletedByRole?: DeletedByRole;

    firstPublishedAt: string;
    publishTime?: string;
    likeable: LikeableEntity;
    visibility: PostVisibility;
    commentability: PostCommentability;

    isPublished: boolean;
    isPRArticle: boolean;
    isRedaction: boolean;
    isCommercial: boolean;
    isActiveAmbasador: boolean;
    isNoAds: boolean;
    isDisplayingAtFeed: boolean;
    commercialDetails?: {
        relatedToDaysWith: string[],
        relatedToAmbassadorOfBrands: number[],
        relatedToTesting: string[],
    };
}

export const BlogArticleSchema = new schema.Entity('photoblog.post', {
    user: UserSchema,
    photos: [PhotoSchema],
    likeable: createLikeableSchema('post'),
});

export interface CommentParent {
    model: string;
    id: number;
}

export interface CommentEntity extends Entity {
    id: number;
    textAST: AST;
    textASTAdditionalData: ASTAdditionalDataFromServer;
    authorUser: UserEntity;
    created: string;
    stickerId: number;
    postId: number;
    positionInPost: number;
    photos: PhotoEntity[];
    targetOwner: UserEntity;
    likeable: LikeableEntity;
    isLikeable: boolean;
    canDelete: boolean;
    deletedAt?: string;
    deletedReason?: string;
    deletedBy?: UserEntity;
    deletedByRole?: DeletedByRole;
    byAdmin: boolean;
}

export const CommentSchema = new schema.Entity('comments', {
    authorUser: UserSchema,
    targetOwner: UserSchema,
    photos: [PhotoSchema],
    likeable: createLikeableSchema('comment'),
    deletedBy: UserSchema,
});

export interface ImageEntity extends Entity {
    postUrl: string;
    username: string;
    title: string;
    commentsCount: number;
    likesCount: number;
    photo: PhotoEntity;
    likeable: LikeableEntity;
    canDelete: boolean;
    canEdit: boolean;
    canRotate: boolean;
    complaintsCount: number;
    createdTime?: string;
}

export const ImageSchema = new schema.Entity('blogImages', {
    photo: PhotoSchema,
    likeable: createLikeableSchema('photoblog-image'),
});

export interface GroupEntity extends Entity {
    categoryId: number;
    name: string;
    description: string;
    coverPhoto: PhotoEntity | null;
    deletedAt: string;
    // deletedReason: string;
    // deletedBy: UserEntity;
    activeMembersCount: number;
    locked: boolean;
    imagesCount: number;
    membersCount: number;
    postsCount: number;
    isDomainGroup: boolean;
    isListedPrivate: boolean;
    isNoAds: boolean;
    showDeletedPosts: boolean;
}

export const GroupSchema = new schema.Entity('blogGroups', {
    coverPhoto: PhotoSchema,
    deletedBy: UserSchema,
});

export enum GroupMemberRoles {
    MEMBER = 0,
    MODERATOR = 1,
}

export interface GroupMemberEntity extends Entity {
    user: UserEntity;
    group: GroupEntity;
    role: GroupMemberRoles;
    createdTime: string;
    blockedTime: string;
    blockedReason: string;
    blockedByUser: UserEntity;
    hideGroupInFeed: boolean;
}

export const GroupMemberSchema = new schema.Entity('blogGroupMembers', {
    user: UserSchema,
    group: GroupSchema,
    blockedByUser: UserSchema,
});

export enum GroupInvitationStatuses {
    PENDING = 0,
    ACCEPTED = 1,
    REFUSED = 2,
}

export interface GroupInvitationEntity extends Entity {
    user: UserEntity;
    group: GroupEntity;
    role: GroupMemberRoles;
    inviter: UserEntity;
    status: GroupInvitationStatuses;
    createdTime: string;
}

export const GroupInvitationSchema = new schema.Entity('blogGroupInvitations', {
    user: UserSchema,
    group: GroupSchema,
    inviter: UserSchema,
});

export enum BlogFeedItemType {
    POST = 'POST',
    POST_LIKE = 'POST_LIKE',
    PHOTOBLOG_LIKE = 'PHOTOBLOG_LIKE', // legacy type
    PROFILE_LIKE = 'PROFILE_LIKE',
    GROUP_MEMBER = 'GROUP_MEMBER',
    WIKI_EXP = 'WIKI_EXP', // legacy type
    CUSTOM = 'CUSTOM', // legacy type
}

export enum BlogPostType {
    ALBUM = 1,
    SHORT_MESSAGE = 2,
    ARTICLE = 3,
    AUTOMATIC_MESSAGE = 4,
    SHARE = 5,
}

export interface BlogPostEntity extends Entity {
    group?: GroupEntity;
    author: UserEntity; // Deprecated, use 'user'
    user: UserEntity;
    created: string;
    lastChanged?: string;
    heading: string;
    titleAST: AST;
    titleASTAdditionalData: ASTAdditionalDataFromServer;
    perexAST: AST; // Deprecated
    perexASTAdditionalData: ASTAdditionalDataFromServer; // Deprecated
    images: ImageEntity[];
    stickerId: number;
    snippet: string;
    likeable: LikeableEntity;
    commentsCount: number;
    sharesCount?: number;
    complaintsCount: number;
    sticky: boolean;
    lastMessageTime: string;
    slug: string;
    popularAddedOn: string;
    noindex: boolean;
    isNoAds: boolean;
    deletedAt?: string;
    deletedReason?: string;
    deletedBy?: UserEntity;
    deletedByRole?: DeletedByRole;
    isDisplayingAtFeed: boolean;
    visibility: PostVisibility;
    wasEdited: boolean;
    type: BlogPostType;
    editedKidData?: {
        name: string;
        age: string;
        gender: string;
    };

    // Album and/or Article related data of this blog post
    contentAST?: AST;
    contentASTAdditionalData?: ASTAdditionalDataFromServer;
    blockedAt?: string;
    blockedReason?: string;
    blockedBy?: string;
    article?: BlogArticleEntity; // TODO to be deleted
    viewsCount?: number;
    photos?: PhotoEntity[]; // TODO rename to articlePhotos
    countTopicContributors?: {[topicSlug: string]: number};
    isRedaction?: boolean;

    // Shared post related data of this blog post
    sharedPost?: BlogPostEntity;

    // Automatic message post related data of this blog post
    automsgTitle?: string;
}

export const BlogPostSchema = new schema.Entity('photoblog.post', {
    likeable: createLikeableSchema('post'),
    group: GroupSchema,
    author: UserSchema, // Deprecated
    user: UserSchema,
    images: [ImageSchema],
    photos: [PhotoSchema],
    deletedBy: UserSchema,
});

export interface UsersByGroupId {
    [groupId: number]: UserEntity[];
}

export interface BlogFeedItemEntity extends Entity {
    itemType: BlogFeedItemType;
    user: UserEntity;
    data: any;
    experience: any;
    groupMember: GroupMemberEntity;
    group: GroupEntity;
    photoblog: any;
    post: BlogPostEntity;
    profile: UserEntity;
    profileUserDomainData: UserDomainDataEntity;
    showAllImages: boolean;
    dateTimeStamp: string;
}

export const BlogFeedItemSchema = new schema.Entity('blogFeedItems', {
    user: UserSchema,
    groupMember: GroupMemberSchema,
    group: GroupSchema,
    post: BlogPostSchema,
    profile: UserSchema,
});
